import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  Smartphone,
  Globe,
  CheckCircle,
  PhoneOff,
  Infinity,
  Euro,
  Search,
  Headphones,
  Users,
} from "lucide-react";
import heroBg from "@/assets/telefonie-gesprekskosten-bg.jpg";
import { lazy, Suspense, useState, useMemo } from "react";
import { ClientOnly } from "vite-react-ssg";
const WorldMapZones = lazy(() => import("@/components/WorldMapZones"));

/* ── Nationale tarieven ── */
const hipPerMinuut = [
  { name: "Onnet (intern)", start: "€ 0,0000", perMin: "€ 0,0000", icon: Users, color: "bg-hero-gradient" },
  { name: "Nationaal Vast", start: "€ 0,0765", perMin: "€ 0,0190", icon: Phone, color: "bg-hero-gradient" },
  { name: "Nationaal Mobiel", start: "€ 0,0765", perMin: "€ 0,0690", icon: Smartphone, color: "bg-hero-gradient" },
  { name: "Servicenummers", start: "service tarief", perMin: "service tarief", icon: Headphones, color: "bg-hero-gradient" },
];

const hipOnbeperkt = [
  { name: "Onnet (intern)", start: "€ 0,0000", perMin: "€ 0,0000", icon: Users, color: "bg-rinqel-blue-gradient" },
  { name: "Nationaal Vast", start: "€ 0,0000", perMin: "€ 0,0000", icon: Phone, color: "bg-rinqel-blue-gradient" },
  { name: "Nationaal Mobiel", start: "€ 0,0000", perMin: "€ 0,0000", icon: Smartphone, color: "bg-rinqel-blue-gradient" },
  { name: "Servicenummers", start: "service tarief", perMin: "service tarief", icon: Headphones, color: "bg-rinqel-blue-gradient" },
];

/* ── Internationale zones ── */
const zones = [
  {
    id: 1,
    name: "Zone 1",
    start: "€ 0,04",
    vast: "€ 0,07",
    mobiel: "€ 0,27",
    gradient: "bg-hero-gradient",
    borderColor: "border-primary/30",
    countries: [
      "Andorra", "Austria", "Belgium", "Bulgaria", "Canada", "China", "Croatia", "Cyprus",
      "Czech Republic", "Denmark", "Estonia", "Finland", "France", "Germany", "Greece",
      "Hungary", "Iceland", "India", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg",
      "Malta", "Norway", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain",
      "Sweden", "Switzerland", "United Kingdom", "United States",
    ],
  },
  {
    id: 2,
    name: "Zone 2",
    start: "€ 0,04",
    vast: "€ 0,34",
    mobiel: "€ 0,50",
    gradient: "bg-rinqel-blue-gradient",
    borderColor: "border-secondary/30",
    countries: [
      "American Samoa", "Argentina", "Aruba", "Australia", "Bonaire", "Brazil", "Cambodia",
      "Chile", "Colombia", "Costa Rica", "Curaçao", "Dominican Republic", "El Salvador",
      "Faroe Islands", "French Guiana", "Gibraltar", "Guadeloupe", "Honduras", "Hong Kong",
      "Indonesia", "Israel", "Jamaica", "Japan", "Korea (South)", "Liechtenstein", "Malaysia",
      "Martinique", "Mexico", "Monaco", "Kosovo", "Netherlands Antilles", "New Zealand",
      "Nicaragua", "Palestine", "Panama", "Peru", "Philippines", "Puerto Rico",
      "Reunion Island", "Russia", "Saba", "San Marino", "Singapore", "South Africa",
      "Sri Lanka", "St. Eustatius", "St. Maarten", "Suriname", "Taiwan", "Thailand",
      "Turkey", "Ukraine", "United Arab Emirates", "Uruguay", "US Virgin Islands",
    ],
  },
  {
    id: 3,
    name: "Zone 3",
    start: "€ 0,04",
    vast: "€ 0,62",
    mobiel: "€ 0,80",
    gradient: "bg-rinqel-green-gradient",
    borderColor: "border-rinqel-green/30",
    countries: [
      "Afghanistan", "Albania", "Algeria", "Angola", "Anguilla", "Antigua & Barbuda",
      "Armenia", "Ascension Island", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh",
      "Barbados", "Belarus", "Belize", "Benin", "Bermuda", "Bhutan", "Bolivia", "Bosnia",
      "Botswana", "British Virgin Islands", "Brunei", "Burkina Faso", "Burundi", "Cameroon",
      "Cape Verde Islands", "Cayman Islands", "Central African Republic", "Chad", "Comoros",
      "Congo", "Congo-DR", "Cuba", "Diego Garcia", "Djibouti", "Dominica", "East Timor",
      "Ecuador", "Egypt", "Equatorial Guinea", "Eritrea", "Ethiopia", "Falkland Islands",
      "Fiji Island", "French Polynesia", "Gabon Republic", "Gambia", "Georgia", "Ghana",
      "Greenland", "Grenada", "Guam", "Guatemala", "Guinea", "Guinea Bissau", "Guyana",
      "Haiti", "Iran", "Iraq", "Ivory Coast", "Jordan", "Kazakhstan", "Kenya",
      "Korea (North)", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Lebanon", "Lesotho",
      "Liberia", "Libya", "Macau", "Macedonia", "Madagascar", "Malawi", "Maldives", "Mali",
      "Mariana Island", "Marshall Islands", "Mauritania", "Mauritius", "Micronesia",
      "Moldova", "Mongolia", "Montenegro", "Montserrat", "Morocco", "Mozambique", "Myanmar",
      "Namibia", "Nepal", "Nevis & St. Kitts", "New Caledonia", "Niger", "Nigeria", "Niue",
      "Norfolk Island", "Oman", "Pakistan", "Palau", "Papua New Guinea", "Paraguay", "Qatar",
      "Rwanda", "Sao Tome", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
      "Sierra Leone", "Solomon Islands", "Somalia", "South Sudan", "St. Helena", "St. Lucia",
      "St. Pierre & Miquelon", "St. Vincent & Grenadines", "Sudan", "Swaziland", "Syria",
      "Tajikistan", "Tanzania", "Togo", "Trinidad & Tobago", "Tunisia", "Turkmenistan",
      "Turks & Caicos", "Tuvalu", "Uganda", "Uzbekistan", "Venezuela", "Vietnam", "Voxbone",
      "Wallis & Futuna", "Western Samoa", "Yemen Arab Republic", "Zambia", "Zimbabwe",
    ],
  },
  {
    id: 4,
    name: "Zone 4",
    start: "€ 0,04",
    vast: "€ 5,00",
    mobiel: "€ 5,00",
    gradient: "bg-gradient-to-br from-destructive to-destructive/80",
    borderColor: "border-destructive/30",
    countries: [
      "Antarctica & Islands", "Australia Satellite Services", "Cook Islands", "Inmarsat",
      "Iridium", "Kiribati", "Nauru", "Russia-Thuraya", "Tokelau", "Tonga", "UPT", "Vanuatu",
    ],
  },
];

const voordelen = [
  {
    icon: Euro,
    title: "Per seconde afgerekend",
    desc: "Je betaalt alleen wat je daadwerkelijk belt — afgerond op de eerstvolgende hele cent. Geen afronding op minuut-basis.",
    color: "bg-hero-gradient",
  },
  {
    icon: Infinity,
    title: "Intern bellen gratis",
    desc: "Collega's bellen met collega's via de centrale? Dat is altijd gratis. Onbeperkt, ongeacht de locatie.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: PhoneOff,
    title: "0800 altijd gratis",
    desc: "Bellen naar 0800-nummers kost je nooit iets. Geen starttarief, geen gesprekstarief.",
    color: "bg-rinqel-green-gradient",
  },
];

/* ── Tarief-kaart component ── */
const TariefCard = ({ item }: { item: { name: string; start: string; perMin: string; icon: any; color: string } }) => {
  const Icon = item.icon;
  return (
    <div className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
      <div className={`${item.color} px-5 py-4 flex items-center gap-3`}>
        <Icon className="h-5 w-5 text-primary-foreground shrink-0" />
        <p className="text-sm font-bold text-primary-foreground leading-tight">{item.name}</p>
      </div>
      <div className="px-5 py-4 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Starttarief</p>
          <p className="font-bold text-foreground">{item.start}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Per minuut</p>
          <p className="font-bold text-foreground">{item.perMin}</p>
        </div>
      </div>
    </div>
  );
};

/* WorldMapSVG is now replaced by the WorldMapZones component */

/* ── Pagina ── */
const TelefonieGesprekskosten = () => {
  const [zoekterm, setZoekterm] = useState("");

  const { filteredZones, openZones } = useMemo(() => {
    if (!zoekterm.trim()) return { filteredZones: zones, openZones: [] as string[] };
    const term = zoekterm.toLowerCase();
    const matched = zones
      .filter((z) => z.countries.some((c) => c.toLowerCase().includes(term)))
      .map((z) => ({
        ...z,
        countries: z.countries, // keep all, highlight matching
      }));
    return {
      filteredZones: matched.length > 0 ? matched : [],
      openZones: matched.map((z) => `zone-${z.id}`),
    };
  }, [zoekterm]);

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[440px] overflow-hidden">
        <img
          src={heroBg}
          alt="VoIP Gesprekskosten Rinqel"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/20" />

        <div className="container relative z-10 flex min-h-[440px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/telefonie"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Telefonie
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Per seconde · Geen verrassingen
              </span>
            </motion.div>

            <motion.h1
              className="mt-4 text-4xl font-bold leading-tight tracking-tight text-white md:text-6xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              VoIP{" "}
              <span className="text-gradient">Gesprekskosten</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Transparant en eerlijk. Gesprekskosten worden per seconde berekend, intern bellen is altijd gratis, en 0800-nummers kosten je nooit iets.
            </motion.p>

            <motion.div
              className="mt-8 flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" size="lg" asChild>
                <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="telefonie-gesprekskosten-hero">Bereken je prijs <ArrowRight className="h-4 w-4" /></TrackedLink>
              </Button>
              <Button className="border border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm font-semibold" size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Gratis advies" trackLocation="telefonie-gesprekskosten-hero">Gratis advies</TrackedLink>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-20 bg-hero-gradient-subtle">
        <div className="container">
          <div className="mb-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full bg-hero-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-primary">Hoe het werkt</span>
              <span className="h-1 w-10 rounded-full bg-hero-gradient" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Eerlijk, transparant en{" "}
              <span className="text-gradient">zonder verrassingen</span>
            </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-3 max-w-4xl mx-auto">
            {voordelen.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className={`relative overflow-hidden flex flex-col gap-3 rounded-2xl p-6 ${v.color} shadow-card`}
                >
                  <div className="absolute -right-4 -bottom-4 h-20 w-20 rounded-full bg-white/10" />
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
                    <Icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div className="relative">
                    <p className="font-bold text-primary-foreground">{v.title}</p>
                    <p className="mt-1 text-sm text-primary-foreground/80">{v.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nationale tarieven */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full bg-rinqel-blue-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary">Nederland</span>
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Nationale gesprekskosten{" "}
              <span className="text-gradient">per minuut</span>
            </h2>
            <p className="mt-2 text-muted-foreground">
              Kies tussen Per Minuut of Onbeperkt NL — afhankelijk van je belgedrag.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2 max-w-5xl">
            {/* HIP Per Minuut */}
            <div>
              <div className="mb-4 flex items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-4 py-1.5 text-sm font-bold text-primary">
                  <Phone className="h-4 w-4" />
                  Per Minuut
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {hipPerMinuut.map((item) => (
                  <TariefCard key={item.name} item={item} />
                ))}
              </div>
            </div>

            {/* HIP Onbeperkt */}
            <div>
              <div className="mb-4 flex items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary/10 border border-secondary/20 px-4 py-1.5 text-sm font-bold text-secondary">
                  <Infinity className="h-4 w-4" />
                  Onbeperkt
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {hipOnbeperkt.map((item) => (
                  <TariefCard key={item.name} item={item} />
                ))}
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-muted-foreground max-w-3xl">
            Prijzen zijn per minuut en exclusief BTW. De kosten van een gesprek worden per gehele seconde berekend en afgerond op de eerstvolgende hele cent.
            Voor servicenummers wordt naast het geldende servicetarief een verkeerstarief in rekening gebracht gelijk aan het nationaal vast tarief.
            Voor 0800-nummers is het verkeerstarief kosteloos.
          </p>
        </div>
      </section>

      {/* Internationaal */}
      <section className="py-16 md:py-20 bg-hero-gradient-subtle">
        <div className="container">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full bg-rinqel-green-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-rinqel-green">Internationaal</span>
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Internationale gesprekskosten{" "}
              <span className="text-gradient">per minuut</span>
            </h2>
            <p className="mt-2 text-muted-foreground">
              Internationale bestemmingen zijn ingedeeld in 4 zones. Starttarief voor alle zones: € 0,04.
            </p>
          </div>

          {/* Wereldkaart (client-only) */}
          <ClientOnly>
            {() => (
              <Suspense fallback={<div className="mx-auto mb-10 max-w-5xl aspect-[2/1]" aria-hidden />}>
                <WorldMapZones />
              </Suspense>
            )}
          </ClientOnly>

          {/* Zone-kaarten */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto mb-8">
            {zones.map((z) => (
              <div key={z.id} className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className={`${z.gradient} px-5 py-4 flex items-center gap-3`}>
                  <Globe className="h-5 w-5 text-primary-foreground shrink-0" />
                  <p className="text-sm font-bold text-primary-foreground">{z.name}</p>
                </div>
                <div className="px-5 py-4 space-y-2">
                  <div className="flex justify-between">
                    <p className="text-xs text-muted-foreground">Starttarief</p>
                    <p className="text-sm font-bold text-foreground">{z.start}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-xs text-muted-foreground">Vast</p>
                    <p className="text-sm font-bold text-foreground">{z.vast}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-xs text-muted-foreground">Mobiel</p>
                    <p className="text-sm font-bold text-foreground">{z.mobiel}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Zoekbalk */}
          <div className="relative mb-6 max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Zoek een land om de zone te vinden…"
              value={zoekterm}
              onChange={(e) => setZoekterm(e.target.value)}
              className="w-full rounded-xl border border-border bg-card pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Accordion met landen per zone */}
          {filteredZones.length > 0 ? (
            <Accordion
              type="multiple"
              defaultValue={openZones}
              value={zoekterm.trim() ? openZones : undefined}
              className="max-w-3xl mx-auto space-y-3"
            >
              {filteredZones.map((z) => (
                <AccordionItem
                  key={z.id}
                  value={`zone-${z.id}`}
                  className={`rounded-xl border ${z.borderColor} bg-card overflow-hidden`}
                >
                  <AccordionTrigger className="px-5 py-3 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-primary-foreground ${z.gradient}`}>
                        {z.id}
                      </span>
                      <span className="font-semibold">{z.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({z.countries.length} landen)
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4">
                    <div className="flex flex-wrap gap-2">
                      {z.countries.map((country) => {
                        const isMatch = zoekterm.trim() && country.toLowerCase().includes(zoekterm.toLowerCase());
                        return (
                          <span
                            key={country}
                            className={`inline-block rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                              isMatch
                                ? "bg-primary text-primary-foreground ring-2 ring-primary/30"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {country}
                          </span>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm max-w-3xl mx-auto">
              Geen landen gevonden voor "{zoekterm}"
            </div>
          )}

          {/* Disclaimer */}
          <p className="mt-8 text-sm text-muted-foreground text-center max-w-3xl mx-auto">
            Prijzen zijn per minuut en exclusief BTW. Kosten worden per gehele seconde berekend en afgerond op de eerstvolgende hele cent.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container">
          <div className="relative overflow-hidden rounded-2xl bg-hero-gradient p-10 md:p-14 text-center shadow-card">
            <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-white/10" />
            <div className="absolute -left-6 bottom-0 h-36 w-36 rounded-full bg-white/5" />
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground">
                Wil je weten wat bellen jou kost?
              </h2>
              <p className="mt-3 text-primary-foreground/80 max-w-xl mx-auto">
                Gebruik de prijscalculator of vraag een vrijblijvende offerte aan — dan rekenen we het precies voor je uit.
              </p>
              <ul className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-primary-foreground/90">
                {["Per seconde afgerekend", "Intern bellen gratis", "0800 altijd gratis"].map((item) => (
                  <li key={item} className="flex items-center gap-1.5">
                    <CheckCircle className="h-4 w-4 text-primary-foreground/70 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <Button className="bg-white text-primary hover:bg-white/90 font-semibold" size="lg" asChild>
                  <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="telefonie-gesprekskosten-cta">Bereken je prijs <ArrowRight className="h-4 w-4" /></TrackedLink>
                </Button>
                <Button className="border border-white/30 bg-white/10 text-primary-foreground hover:bg-white/20 font-semibold" size="lg" asChild>
                  <TrackedLink to="/contact" trackLabel="Offerte aanvragen" trackLocation="telefonie-gesprekskosten-cta">Offerte aanvragen</TrackedLink>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TelefonieGesprekskosten;
